import { useMemo } from "react";
import {
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Area,
  ComposedChart,
  Brush
} from "recharts";

const EquityCurveChart = ({ data, showMetric, theme }) => {
  const chartData = useMemo(() => {
    return data.map((item) => ({
      name: item.label,
      value: showMetric === "percentage" ? item.percentage : item.balance,
      percentage: item.percentage,
      balance: item.balance,
      periodReturn: item.periodReturn,
      periodReturnPercent: item.periodReturnPercent
    }));
  }, [data, showMetric]);

  const CustomTooltip = ({ active, payload }) => {
    if (!active || !payload || !payload.length) return null;

    const data = payload[0].payload;

    return (
      <div
        style={{
          backgroundColor: theme[0] === "dark" ? "#1e1f23" : "#ffffff",
          border: `1px solid ${theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}`,
          borderRadius: "8px",
          padding: "12px",
          boxShadow: "0 4px 6px rgba(0, 0, 0, 0.1)",
        }}
      >
        <div
          style={{
            fontSize: "13px",
            fontWeight: 600,
            marginBottom: "8px",
            color: theme[0] === "dark" ? "#e5e7eb" : "#374151",
          }}
        >
          {data.name}
        </div>
        <div
          style={{
            fontSize: "12px",
            color: theme[0] === "dark" ? "#9ca3af" : "#6b7280",
            display: "flex",
            flexDirection: "column",
            gap: "4px",
          }}
        >
          <div>
            <span style={{ fontWeight: 500 }}>Balance:</span>{" "}
            ${data.balance?.toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
          <div>
            <span style={{ fontWeight: 500 }}>Cumulative:</span>{" "}
            <span
              style={{
                color: data.percentage >= 0 ? "#10b981" : "#ef4444",
              }}
            >
              {data.percentage >= 0 ? "+" : ""}
              {data.percentage?.toFixed(2)}%
            </span>
          </div>
          <div>
            <span style={{ fontWeight: 500 }}>Period Return:</span>{" "}
            <span
              style={{
                color: data.periodReturnPercent >= 0 ? "#10b981" : "#ef4444",
              }}
            >
              {data.periodReturnPercent >= 0 ? "+" : ""}
              {data.periodReturnPercent?.toFixed(2)}% ($
              {data.periodReturn?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })})
            </span>
          </div>
        </div>
      </div>
    );
  };

  const yAxisFormatter = (value) => {
    if (showMetric === "percentage") {
      return `${value.toFixed(0)}%`;
    } else {
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      }
      return `$${value.toFixed(0)}`;
    }
  };

  const getGradientColor = () => {
    if (chartData.length === 0) return "#3b82f6";
    const lastValue = chartData[chartData.length - 1].value;
    const firstValue = chartData[0].value;
    return lastValue >= firstValue ? "#10b981" : "#ef4444";
  };

  const gradientColor = getGradientColor();

  return (
    <ResponsiveContainer width="100%" height="100%">
      <ComposedChart
        data={chartData}
        margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
      >
        <defs>
          <linearGradient id="colorValue" x1="0" y1="0" x2="0" y2="1">
            <stop offset="5%" stopColor={gradientColor} stopOpacity={0.3} />
            <stop offset="95%" stopColor={gradientColor} stopOpacity={0} />
          </linearGradient>
        </defs>
        <CartesianGrid
          strokeDasharray="3 3"
          stroke={theme[0] === "dark" ? "#2d2e33" : "#e5e7eb"}
          vertical={false}
        />
        <XAxis
          dataKey="name"
          stroke={theme[0] === "dark" ? "#6b7280" : "#9ca3af"}
          style={{ fontSize: "12px" }}
          tickLine={false}
        />
        <YAxis
          stroke={theme[0] === "dark" ? "#6b7280" : "#9ca3af"}
          style={{ fontSize: "12px" }}
          tickLine={false}
          tickFormatter={yAxisFormatter}
        />
        <Tooltip content={<CustomTooltip />} />
        <Area
          type="monotone"
          dataKey="value"
          stroke={gradientColor}
          strokeWidth={2}
          fillOpacity={1}
          fill="url(#colorValue)"
        />
        {chartData.length > 20 && (
          <Brush
            dataKey="name"
            height={30}
            stroke={theme[0] === "dark" ? "#3b82f6" : "#2563eb"}
            fill={theme[0] === "dark" ? "#1e1f23" : "#f9fafb"}
            travellerWidth={10}
          />
        )}
      </ComposedChart>
    </ResponsiveContainer>
  );
};

export default EquityCurveChart;
